import "./ClientTable.scss";

import { Column, Table } from "@appkit4/react-components";
import type { CSSProperties, ReactNode } from "react";
import { useMemo, useState } from "react";

import { formatDisplayDate } from "../../../utils/dateDisplay";
import { EmptyState, type EmptyStateProps } from "../EmptyState";

type ClientTableAlign = "left" | "center" | "right";
type ClientTableSlot = "start" | "end";

export interface ClientTableColumn<T extends object> {
  id: string;
  header: ReactNode;
  accessor?: keyof T;
  renderCell?: (row: T, index: number) => ReactNode;
  sortable?: boolean;
  sortAccessor?: keyof T | ((row: T) => string | number);
  width?: string;
  align?: ClientTableAlign;
  slot?: ClientTableSlot;
  headerClassName?: string;
  cellClassName?: string;
  headerStyle?: CSSProperties;
  cellStyle?: CSSProperties;
}

export interface ClientTableProps<T extends object> {
  data: T[];
  columns: ClientTableColumn<T>[];
  rowKey?: (row: T, index: number) => string | number;
  className?: string;
  style?: CSSProperties;
  condensed?: boolean;
  striped?: boolean;
  emptyState?: EmptyStateProps;
  rowClassName?: (row: T, index: number) => string | undefined;
  onRowClick?: (row: T, index: number) => void;
}

interface InternalClientTableRow<T extends object> extends Record<string, unknown> {
  __original: T;
  __index: number;
  __rowId: string | number;
  __rowClassName?: string;
}

function compareSortValues(left: unknown, right: unknown) {
  if (left == null) return right == null ? 0 : 1;
  if (right == null) return -1;
  if (typeof left === "number" && typeof right === "number") return left - right;
  if (left instanceof Date && right instanceof Date) return left.getTime() - right.getTime();
  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: "base" });
}

function formatCellValue(value: unknown) {
  if (value == null) {
    return "";
  }

  if (typeof value === "string" || typeof value === "number") {
    return value;
  }

  if (value instanceof Date) {
    return formatDisplayDate(value);
  }

  return String(value);
}

function ClientTable<T extends object>({
  data,
  columns,
  rowKey,
  className = "",
  style,
  condensed = false,
  striped = false,
  emptyState,
  rowClassName,
  onRowClick,
}: ClientTableProps<T>) {
  const tableData = useMemo<Array<InternalClientTableRow<T>>>(() => {
    return data.map((row, index) => {
      const mappedRow: InternalClientTableRow<T> = {
        __original: row,
        __index: index,
        __rowId: rowKey?.(row, index) ?? index,
        __rowClassName: rowClassName?.(row, index),
      };

      columns.forEach((column) => {
        if (column.sortAccessor != null) {
          mappedRow[column.id] = typeof column.sortAccessor === "function"
            ? column.sortAccessor(row)
            : row[column.sortAccessor];
          return;
        }

        if (column.accessor != null) {
          mappedRow[column.id] = row[column.accessor];
          return;
        }

        // Ensure every column has a key so AppKit Table renders the cell
        mappedRow[column.id] = null;
      });

      return mappedRow;
    });
  }, [columns, data, rowClassName, rowKey]);

  const wrapperClasses = ["shared-client-table", className].filter(Boolean).join(" ");

  const [sortState, setSortState] = useState<{ key: string; phase: 1 | 2 } | null>(null);
  const sortedTableData = useMemo(() => {
    if (sortState == null) return tableData;
    const sortColumn = columns.find((column) => column.id === sortState.key);
    if (sortColumn == null) return tableData;

    const direction = sortState.phase === 1 ? 1 : -1;
    return [...tableData].sort((left, right) => {
      const result = compareSortValues(left[sortColumn.id], right[sortColumn.id]);
      return result === 0 ? left.__index - right.__index : result * direction;
    });
  }, [columns, sortState, tableData]);

  const handleSort = (key: string) => {
    setSortState((current) => ({
      key,
      phase: current?.key === key && current.phase === 1 ? 2 : 1,
    }));
  };

  // When the table empties out it unmounts AppKit's <Table>; if rows return
  // without ClientTable itself unmounting, AppKit remounts a fresh, unsorted
  // table while the header would otherwise still show the stale indicator.
  // Reset the tracked sort state on the empty transition (adjusting state
  // during render, per the React "storing previous value" pattern) so the
  // repopulated table and its headers start in a consistent, unsorted state.
  const isEmpty = data.length === 0;
  const [prevIsEmpty, setPrevIsEmpty] = useState(isEmpty);
  if (isEmpty !== prevIsEmpty) {
    setPrevIsEmpty(isEmpty);
    if (isEmpty && sortState !== null) {
      setSortState(null);
    }
  }

  if (data.length === 0 && emptyState != null) {
    return (
      <div className={wrapperClasses} style={style}>
        <div className="shared-client-table__empty-state">
          <EmptyState {...emptyState} />
        </div>
      </div>
    );
  }

  return (
    <div className={wrapperClasses} style={style}>
      <Table
        className="shared-client-table__table"
        originalData={sortedTableData}
        condensed={condensed}
        striped={striped}
        disableDefaultSort
        // AppKit's Table only renders its <thead> when hasTitle is true; every
        // ClientTable column defines a header, so the header row must always show.
        hasTitle
        onRowClick={(_, rowIndex, rowData) => {
          const currentRow = rowData as InternalClientTableRow<T>;
          onRowClick?.(currentRow.__original, rowIndex);
        }}
      >
        {columns.map((column) => {
          const sortPhase = column.sortable && sortState?.key === column.id ? sortState.phase : 0;
          const headerClassName = [
            "shared-client-table__header",
            column.align != null ? `shared-client-table__header--${column.align}` : "",
            column.sortable ? `shared-client-table__header--sort-phase-${sortPhase}` : "",
            column.headerClassName ?? "",
          ].filter(Boolean).join(" ");

          return (
            <Column
              key={column.id}
              field={column.id}
              slot={column.slot}
              className={headerClassName}
              style={{
                width: column.width,
                minWidth: column.width,
                ...column.headerStyle,
              }}
              renderCell={(rowData) => {
                const currentRow = rowData as InternalClientTableRow<T>;
                const content = column.renderCell != null
                  ? column.renderCell(currentRow.__original, currentRow.__index)
                  : column.accessor != null
                    ? formatCellValue(currentRow.__original[column.accessor])
                    : null;

                const cellClasses = [
                  "shared-client-table__cell",
                  column.align != null ? `shared-client-table__cell--${column.align}` : "",
                  column.cellClassName ?? "",
                  currentRow.__rowClassName != null ? `${currentRow.__rowClassName}__cell` : "",
                ].filter(Boolean).join(" ");

                return (
                  <div className={cellClasses} style={column.cellStyle}>
                    {content}
                  </div>
                );
              }}
            >
              {column.sortable ? (
                <button
                  type="button"
                  className="shared-client-table__sort-button"
                  aria-label={`Sort by ${typeof column.header === "string" || typeof column.header === "number" ? column.header : column.id}${sortPhase === 1 ? " ascending" : sortPhase === 2 ? " descending" : ""}`}
                  aria-pressed={sortState?.key === column.id}
                  onClick={() => handleSort(column.id)}
                >
                  <span>{column.header}</span>
                  <span className="sort-icon-wrapper" aria-hidden="true" />
                </button>
              ) : (
                column.header
              )}
            </Column>
          );
        })}
      </Table>
    </div>
  );
}

export default ClientTable;
export { ClientTable };
